/**
 * PeriodTabs - Monthly/Quarterly/Yearly billing period toggle.
 *
 * Renders a pill-style toggle matching the reference design:
 * [Mensal | Trimestral | Anual] with primary bg on active tab.
 * Hides periods that no plan offers (price = 0 for all plans).
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/contexts/LanguageContext';
import type { BillingPeriod } from '@/types';

interface PeriodTabsProps {
  period: BillingPeriod;
  onPeriodChange: (period: BillingPeriod) => void;
  availablePeriods?: BillingPeriod[];
}

const PERIODS: { value: BillingPeriod; labelKey: string }[] = [
  { value: 'MONTHLY', labelKey: 'plans.monthly' },
  { value: 'QUARTERLY', labelKey: 'plans.quarterly' },
  { value: 'YEARLY', labelKey: 'plans.yearly' },
];

const PeriodTabs: React.FC<PeriodTabsProps> = ({ period, onPeriodChange, availablePeriods }) => {
  const { t } = useTranslation();

  const visiblePeriods = availablePeriods
    ? PERIODS.filter(({ value }) => availablePeriods.includes(value))
    : PERIODS;

  if (visiblePeriods.length <= 1) return null;

  return (
    <div className="inline-flex rounded-lg bg-muted p-1">
      {visiblePeriods.map(({ value, labelKey }) => (
        <button
          key={value}
          type="button"
          onClick={() => onPeriodChange(value)}
          className={cn(
            'px-5 py-2 rounded-md text-sm font-medium transition-colors',
            period === value
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {t(labelKey)}
        </button>
      ))}
    </div>
  );
};

export default PeriodTabs;
